import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaEnvelope,
  FaExclamationCircle,
  FaCheck,
} from "react-icons/fa";
import { motion } from "framer-motion";
import useTranslation from "../hooks/useTranslation";
import LanguageSelector from "../components/LanguageSelector";

const ForgotPassword = () => {
  const { t, currentLanguage } = useTranslation();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setMessage("");

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t("invalidEmail"));
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/forgot-password`,
        { email, language: currentLanguage },
      );

      if (response.data.success) {
        setMessage(t(response.data.message));
      } else {
        setError(t(response.data.message));
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "unexpectedError";
      setError(t(errorMsg));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (error || message) {
      const timer = setTimeout(() => {
        setError("");
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, message]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-black via-gray-900 to-black p-4 relative">
      <motion.div
        className="relative bg-gray-900/80 border border-green-500/30 rounded-2xl shadow-lg shadow-green-500/30 p-8 max-w-md w-full backdrop-blur-xl"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Glow exterior */}
        <div className="absolute inset-0 rounded-2xl border border-green-500/20 shadow-[0_0_40px_rgba(34,197,94,0.4)] pointer-events-none"></div>

        {/* Feedback error / success */}
        {(error || message) && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center bg-black/70 z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className={`p-4 rounded-lg shadow-lg max-w-sm w-full mx-4 ${
                error ? "bg-red-600/90" : "bg-green-600/90"
              }`}
            >
              <p className="font-bold text-white text-center flex items-center justify-center gap-2">
                {error ? <FaExclamationCircle /> : <FaCheck />}
                {error || message}
              </p>
            </div>
          </motion.div>
        )}

        {/* Language selector */}
        <div className="absolute -top-3 -right-3 z-20">
          <LanguageSelector />
        </div>

        {/* Go back */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 text-gray-400 hover:text-white transition-colors"
          aria-label={t("goBack")}
        >
          <FaArrowLeft size={20} className="text-green-500" />
        </button>

        {/* Titlu */}
        <div className="text-center mb-6 pt-6">
          {/* <motion.div
            className="mx-auto w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mb-4"
            initial={{ scale: 0.9 }}
            animate={{ scale: [0.9, 1, 0.95, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          > */}
          <FaEnvelope className="mx-auto w-8 h-8 text-green-400 rounded-full flex items-center justify-center mb-2" />
          {/* </motion.div> */}
          <h1 className="text-3xl font-bold text-green-400 mb-2 text-center tracking-wide">
            {t("resetPassword")}
          </h1>
          {/* <motion.h1
            className="text-3xl font-bold text-green-300 mb-2 tracking-wide"
            initial={{ opacity: 1, textShadow: "0 0 8px rgba(0,255,150,0.7)" }}
            animate={{
              opacity: [1, 0.8, 1, 0.6, 1, 0.9, 1],
              textShadow: [
                "0 0 8px rgba(0,255,150,0.7)",
                "0 0 20px rgba(0,255,150,1)",
                "0 0 12px rgba(0,255,150,0.8)",
                "0 0 30px rgba(0,255,150,0.9)",
                "0 0 10px rgba(0,255,150,0.6)",
              ],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            {t("resetPassword")}
          </motion.h1> */}
          <p className="text-gray-400 mt-2">{t("enterEmailForReset")}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-300 mb-1"
            >
              {t("emailAddress")}
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("emailPlaceholder")}
              autoComplete="email"
              className="w-full px-4 py-3 bg-gray-800/60 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-green-400 focus:border-green-400 placeholder-gray-400 transition"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-3 px-4 rounded-lg font-bold transition-all duration-200 flex items-center justify-center text-white ${
              isLoading
                ? "bg-gray-600 cursor-not-allowed"
                : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-[0_0_20px_rgba(34,197,94,0.6)]"
            }`}
            aria-label={isLoading ? t("processing") : t("sendResetLink")}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-4 border-green-400 border-t-transparent rounded-full animate-spin"></div>
                <span>{t("processing")}</span>
              </div>
            ) : (
              t("sendResetLink")
            )}
          </button>
        </form>

        <p className="text-gray-400 text-sm mt-6 text-center">
          {t("rememberPassword")}{" "}
          <button
            onClick={() => navigate("/login")}
            className="text-green-400 hover:text-green-300 underline focus:outline-none"
            aria-label={t("signInLink")}
          >
            {t("signInLink")}
          </button>
        </p>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
